import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { ClipLoader } from "react-spinners";
import { BsThreeDotsVertical } from "react-icons/bs";
import { RxCross2 } from "react-icons/rx";
import { FiFileText } from "react-icons/fi";
import TextTranslate from "../../../../utils/TextTranslate";
import { useTranslateText } from "../../../../hooks/useTranslateText";
import { formatBytesShort } from "../../../../components/DocMessageBubble";
import { MESSAGE_TXT_NAME } from "../../../../utils/textBubbleSplit";

type Props = {
  open: boolean;
  text: string;
  sending?: boolean;
  onCancel: () => void;
  /** Send with current (possibly renamed) file name */
  onConfirm: (fileName: string) => void;
};

function ensureTxtFileName(raw: string): string {
  const base = String(raw || "")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, " ");
  if (!base) return MESSAGE_TXT_NAME;
  if (/\.txt$/i.test(base)) return base;
  return `${base}.txt`;
}

/**
 * TG-style "Send as a file" sheet for oversized compose text → Message.txt.
 * No caption yet. ⋮ → Rename file.
 */
export default function SendLongTextAsFileModal({
  open,
  text,
  sending = false,
  onCancel,
  onConfirm,
}: Props) {
  const translate = useTranslateText();
  const [fileName, setFileName] = useState(MESSAGE_TXT_NAME);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(MESSAGE_TXT_NAME);
  const renameRef = useRef<HTMLInputElement>(null);

  const bytes = new Blob([text || ""]).size;
  const sizeLabel = formatBytesShort(bytes) || "0B";

  useEffect(() => {
    if (!open) return;
    setFileName(MESSAGE_TXT_NAME);
    setDraftName(MESSAGE_TXT_NAME);
    setRenaming(false);
  }, [open, text]);

  useEffect(() => {
    if (renaming) {
      renameRef.current?.focus();
      renameRef.current?.select();
    }
  }, [renaming]);

  const commitRename = () => {
    setFileName(ensureTxtFileName(draftName));
    setRenaming(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !sending && onCancel()}
      className="relative z-[80]"
    >
      <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md overflow-hidden rounded-2xl border border-borderColor bg-modalBg shadow-2xl">
          <div className="flex items-center justify-between px-5 pb-3 pt-4">
            <DialogTitle className="text-lg font-semibold text-darkText">
              <TextTranslate text="Send as a file" />
            </DialogTitle>
          </div>

          {/* Document bubble row */}
          <div className="mx-4 mb-4 flex items-center gap-3 rounded-xl bg-secondary/80 px-3 py-3 ring-1 ring-borderColor">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--theme-color,#50a2e3)] text-[var(--theme-text-color,#fff)]"
              aria-hidden
            >
              <FiFileText className="h-6 w-6" />
            </div>

            <div className="min-w-0 flex-1">
              {renaming ? (
                <input
                  ref={renameRef}
                  value={draftName}
                  disabled={sending}
                  onChange={(e) => setDraftName(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitRename();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setDraftName(fileName);
                      setRenaming(false);
                    }
                  }}
                  className="w-full rounded-md border border-[var(--theme-color,#50a2e3)] bg-transparent px-2 py-1 text-sm font-semibold text-darkText outline-none"
                  aria-label={translate("Rename file")}
                />
              ) : (
                <p className="truncate text-sm font-semibold text-darkText">
                  {fileName}
                </p>
              )}
              <p className="mt-0.5 text-[12px] tabular-nums text-lightText">
                {sizeLabel}
              </p>
            </div>

            <Menu as="div" className="relative shrink-0">
              <MenuButton
                disabled={sending}
                className="grid h-9 w-9 place-content-center rounded-full text-lightText transition hover:bg-black/5 hover:text-darkText disabled:opacity-40 dark:hover:bg-white/10"
                aria-label={translate("File options")}
              >
                <BsThreeDotsVertical className="h-5 w-5" />
              </MenuButton>
              <MenuItems
                anchor="bottom end"
                className="z-[90] w-44 origin-top-right rounded-xl border border-borderColor bg-modalBg p-1 text-sm shadow-xl transition duration-150 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
              >
                <MenuItem>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={() => {
                        setDraftName(fileName.replace(/\.txt$/i, "") || "Message");
                        setRenaming(true);
                      }}
                      className={`flex w-full items-center rounded-lg px-3 py-2 text-left text-darkText ${
                        focus ? "bg-secondary" : ""
                      }`}
                    >
                      <TextTranslate text="Rename file" />
                    </button>
                  )}
                </MenuItem>
              </MenuItems>
            </Menu>

            <button
              type="button"
              disabled={sending}
              onClick={onCancel}
              className="grid h-9 w-9 shrink-0 place-content-center rounded-full text-lightText transition hover:bg-black/5 hover:text-darkText disabled:opacity-40 dark:hover:bg-white/10"
              aria-label={translate("Remove")}
            >
              <RxCross2 className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center justify-end gap-1 px-3 pb-3 pt-1">
            <button
              type="button"
              disabled={sending}
              onClick={onCancel}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-[var(--theme-color,#50a2e3)] transition hover:bg-[var(--theme-color,#50a2e3)]/10 disabled:opacity-50"
            >
              <TextTranslate text="Cancel" />
            </button>
            <button
              type="button"
              disabled={sending || !text}
              onClick={() => onConfirm(ensureTxtFileName(fileName))}
              className="inline-flex min-w-[5.5rem] items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-[var(--theme-color,#50a2e3)] transition hover:bg-[var(--theme-color,#50a2e3)]/10 disabled:opacity-50"
            >
              {sending ? (
                <ClipLoader size={16} color="var(--theme-color,#50a2e3)" />
              ) : (
                <TextTranslate text="Send" />
              )}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
